// Popup editor for a box's subitems (see Box.parentBoxId's own comment in
// flowchart.types.ts). Renders subitems as small blocks connected by dependency
// arrows (an auto-laid-out DAG, not a free-drag canvas — column = dependency depth,
// so parallel branches land side by side): dependencies are still chosen from a
// dropdown (which also prevents cycles, via arrowRouting's wouldCreateCycle), not
// drawn by hand, deliberately avoiding reimplementing the main canvas's
// arrow-drawing/routing machinery for what only needs to LOOK like boxes+arrows here.
// Scheduling math is delegated entirely to lib/gantt.ts's calcSchedule (see
// CLAUDE.md: never re-derive is24h/scheduling logic elsewhere) — this component
// only renders its result. The layering below is pure visual layout, not scheduling.
import React, { useMemo } from 'react';
import { X, Trash2 } from 'lucide-react';
import { useT } from './i18n/index';
import { calcSchedule } from './lib/gantt';
import { wouldCreateCycle } from './lib/arrowRouting';
import type { Box, Arrow, DurationUnit } from './types/index';

interface SubitemsPanelProps {
  parentBox: Box;
  subitems: Box[];
  dependencyArrows: Arrow[]; // arrows where both fromBox/toBox are among `subitems`
  durationUnit: DurationUnit;
  fullDaySchedule: boolean;
  workHoursOnly: boolean;
  ganttWorkDays: number[];
  ganttNonWorkingDays: string[];
  ganttStartDate: Date;
  workDayStartHour: number;
  workDayHoursPerDay: number;
  onAddSubitem: () => void;
  onUpdateSubitem: (id: number, patch: Partial<Box>) => void;
  onDeleteSubitem: (id: number) => void;
  onAddDependency: (fromBox: number, toBox: number) => void;
  onRemoveDependency: (arrowId: number) => void;
  onClose: () => void;
}

export default function SubitemsPanel({
  parentBox, subitems, dependencyArrows,
  durationUnit, fullDaySchedule, workHoursOnly, ganttWorkDays, ganttNonWorkingDays,
  ganttStartDate, workDayStartHour, workDayHoursPerDay,
  onAddSubitem, onUpdateSubitem, onDeleteSubitem, onAddDependency, onRemoveDependency,
  onClose,
}: SubitemsPanelProps) {
  const { t } = useT();

  const scheduleOptions = {
    durationUnit, fullDaySchedule, workHoursOnly, workDays: ganttWorkDays,
    nonWorkingDays: ganttNonWorkingDays, startDate: ganttStartDate,
    dayStartHour: workDayStartHour, hoursPerDay: workDayHoursPerDay,
  };

  // Local preview: same calcSchedule call applySubitemRollup makes internally for
  // this exact parent, so the start/end shown here always matches the real schedule.
  const tasks = useMemo(
    () => calcSchedule(
      subitems,
      dependencyArrows.map(a => ({ id: a.id, fromBox: a.fromBox, toBox: a.toBox })),
      scheduleOptions,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subitems, dependencyArrows, durationUnit, fullDaySchedule, workHoursOnly, ganttWorkDays, ganttNonWorkingDays, ganttStartDate, workDayStartHour, workDayHoursPerDay],
  );
  const taskById = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks]);
  const totalSpan = tasks.length > 0
    ? Math.max(...tasks.map(t => t.start + t.duration)) - Math.min(...tasks.map(t => t.start))
    : 0;
  const unitLabel = durationUnit === 'hours' ? 'h' : durationUnit === 'weeks' ? 'w' : 'd';

  const depsFor = (subitemId: number) => dependencyArrows.filter(a => a.toBox === subitemId);

  const availableDepOptions = (subitemId: number) => {
    const existing = new Set(depsFor(subitemId).map(a => a.fromBox));
    return subitems.filter(s =>
      s.id !== subitemId &&
      !existing.has(s.id) &&
      !wouldCreateCycle(s.id, subitemId, dependencyArrows),
    );
  };

  // ── Visual layout: column = dependency depth (longest path from a source),
  // row = order within that column. Parallel branches (no dependency between them)
  // land in the same column, side by side — not one long vertical chain. Purely a
  // display concern, unrelated to the actual schedule (calcSchedule above already
  // handles that); wouldCreateCycle already keeps the dependency graph acyclic, but
  // the `visiting` guard below keeps this safe (returns 0) even if that were ever
  // violated, instead of infinite-looping.
  const BOX_W = 168, BOX_H = 112, COL_GAP = 56, ROW_GAP = 14, PAD = 16;
  const layout = useMemo(() => {
    const predsOf = new Map<number, number[]>();
    subitems.forEach(s => predsOf.set(s.id, []));
    dependencyArrows.forEach(a => predsOf.get(a.toBox)?.push(a.fromBox));

    const column = new Map<number, number>();
    const visiting = new Set<number>();
    const computeColumn = (id: number): number => {
      if (column.has(id)) return column.get(id)!;
      if (visiting.has(id)) return 0;
      visiting.add(id);
      const preds = predsOf.get(id) || [];
      const c = preds.length === 0 ? 0 : Math.max(...preds.map(computeColumn)) + 1;
      visiting.delete(id);
      column.set(id, c);
      return c;
    };
    subitems.forEach(s => computeColumn(s.id));

    const byColumn = new Map<number, Box[]>();
    subitems.forEach(s => {
      const c = column.get(s.id) ?? 0;
      if (!byColumn.has(c)) byColumn.set(c, []);
      byColumn.get(c)!.push(s);
    });

    const pos = new Map<number, { x: number; y: number }>();
    let maxCol = 0, maxRows = 0;
    byColumn.forEach((list, c) => {
      maxCol = Math.max(maxCol, c);
      maxRows = Math.max(maxRows, list.length);
      list.forEach((s, row) => {
        pos.set(s.id, { x: PAD + c * (BOX_W + COL_GAP), y: PAD + row * (BOX_H + ROW_GAP) });
      });
    });

    return {
      pos,
      width: PAD * 2 + (maxCol + 1) * BOX_W + maxCol * COL_GAP,
      height: PAD * 2 + Math.max(1, maxRows) * BOX_H + Math.max(0, maxRows - 1) * ROW_GAP,
    };
  }, [subitems, dependencyArrows]);

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500 }}
        onClick={onClose}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%)',
        background: 'white', borderRadius: 12,
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        zIndex: 501, width: 720, maxWidth: '92vw',
        display: 'flex', flexDirection: 'column',
        maxHeight: '85vh', overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t('subitemsPanelTitle')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{parentBox.title || t('subitemUntitled')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '12px 20px 4px' }}>
          <button
            onClick={onAddSubitem}
            style={{ background: 'none', border: '1px dashed #9ca3af', borderRadius: 6, padding: '6px 12px', fontSize: 13, color: '#374151', cursor: 'pointer' }}
          >
            {t('subitemAdd')}
          </button>
        </div>

        <div style={{ padding: '12px 20px 20px', overflow: 'auto' }}>
          {subitems.length === 0 ? (
            <div style={{ fontSize: 13, color: '#9ca3af', padding: '12px 0' }}>{t('subitemEmptyState')}</div>
          ) : (
            <div style={{ position: 'relative', width: layout.width, height: layout.height }}>
              <svg
                width={layout.width}
                height={layout.height}
                style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
              >
                <defs>
                  <marker id="subitem-arrow-head" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                    <path d="M0,0 L6,3 L0,6 Z" fill="#9ca3af" />
                  </marker>
                </defs>
                {dependencyArrows.map(a => {
                  const from = layout.pos.get(a.fromBox);
                  const to = layout.pos.get(a.toBox);
                  if (!from || !to) return null;
                  const x1 = from.x + BOX_W, y1 = from.y + BOX_H / 2;
                  const x2 = to.x, y2 = to.y + BOX_H / 2;
                  const midX = (x1 + x2) / 2;
                  return (
                    <path
                      key={a.id}
                      d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2 - 7} ${y2}`}
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth={1.5}
                      markerEnd="url(#subitem-arrow-head)"
                    />
                  );
                })}
              </svg>

              {subitems.map(s => {
                const p = layout.pos.get(s.id);
                if (!p) return null;
                const task = taskById.get(s.id);
                const deps = depsFor(s.id);
                const options = availableDepOptions(s.id);
                return (
                  <div
                    key={s.id}
                    style={{
                      position: 'absolute', left: p.x, top: p.y, width: BOX_W, height: BOX_H,
                      border: '1px solid #d1d5db', borderRadius: 8, padding: 8, background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      display: 'flex', flexDirection: 'column', gap: 4, overflow: 'visible',
                    }}
                  >
                    {/* UID badge (bottom-left) — muted gray, matching CanvasBox's badge for the same field */}
                    {s.uid && (
                      <div
                        className="pointer-events-none border-2 border-white shadow-md"
                        style={{ position: 'absolute', zIndex: 1, background: '#9ca3af', borderRadius: 4, padding: '1px 5px', bottom: -8, left: -8, maxWidth: 90, overflow: 'hidden' }}
                        title={s.uid}
                      >
                        <span style={{ color: 'white', fontSize: 8, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{s.uid}</span>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={s.title}
                        placeholder={t('subitemUntitled')}
                        // title/text kept in sync, matching useBoxActions' updateTitle convention.
                        onChange={(e) => onUpdateSubitem(s.id, { title: e.target.value, text: e.target.value })}
                        // HTML title attribute: the box is fixed-width and most subitem titles
                        // run longer than it, with no way to see the rest short of clicking in
                        // and scrolling the input's own text — this surfaces the full title as
                        // a native hover tooltip instead.
                        title={s.title || undefined}
                        style={{ flex: 1, minWidth: 0, fontSize: 12, padding: '3px 5px', border: '1px solid #d1d5db', borderRadius: 5 }}
                      />
                      <button
                        onClick={() => onDeleteSubitem(s.id)}
                        title={t('delete')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2, flexShrink: 0 }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>

                    <div style={{ display: 'flex', gap: 4 }}>
                      <input
                        type="text"
                        value={s.duration}
                        placeholder={t('subitemDuration')}
                        // \d* (not \d+) so editing the number while a unit suffix stays in place
                        // (e.g. "6d" -> select/backspace the 6 -> briefly "d" -> type "7") doesn't
                        // get rejected mid-edit — matches CanvasBox.tsx's own live duration input
                        // for exactly this reason (a stricter \d+ is only used at commit time, in
                        // useBoxActions' updateDur, which this panel's onChange bypasses entirely).
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v !== '' && !/^\d*(?:\.\d*)?[hdwHDW]?$/.test(v)) return;
                          onUpdateSubitem(s.id, { duration: v });
                        }}
                        style={{ width: 44, fontSize: 11, padding: '2px 5px', border: '1px solid #d1d5db', borderRadius: 5 }}
                      />
                      <input
                        type="text"
                        value={s.uid || ''}
                        placeholder={t('uidLabel')}
                        onChange={(e) => onUpdateSubitem(s.id, { uid: e.target.value || undefined })}
                        style={{ flex: 1, minWidth: 0, fontSize: 11, padding: '2px 5px', border: '1px solid #d1d5db', borderRadius: 5 }}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', overflowY: 'auto', flex: 1 }}>
                      {deps.map(a => {
                        const pred = subitems.find(sub => sub.id === a.fromBox);
                        return (
                          <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: '#f3f4f6', borderRadius: 10, padding: '1px 5px', fontSize: 10 }}>
                            {pred?.title || t('subitemUntitled')}
                            <button
                              onClick={() => onRemoveDependency(a.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', padding: 0 }}
                            >
                              <X size={9} />
                            </button>
                          </span>
                        );
                      })}
                      {options.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => {
                            const fromId = parseInt(e.target.value, 10);
                            if (fromId) onAddDependency(fromId, s.id);
                          }}
                          style={{ fontSize: 10, padding: '1px 4px', border: '1px dashed #d1d5db', borderRadius: 5, maxWidth: '100%', color: '#6b7280' }}
                        >
                          <option value="" disabled>{t('subitemAddDependency')}</option>
                          {options.map(o => (
                            <option key={o.id} value={o.id}>{o.title || t('subitemUntitled')}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    {task && (
                      <div style={{ fontSize: 9, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {Math.round(task.start * 100) / 100}{unitLabel} → {Math.round((task.start + task.duration) * 100) / 100}{unitLabel}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {t('subitemTotalDuration')}: <strong>{Math.round(totalSpan * 100) / 100}{unitLabel}</strong>
          </div>
          <button
            onClick={onClose}
            style={{ background: '#111827', color: 'white', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 13, cursor: 'pointer' }}
          >
            {t('close')}
          </button>
        </div>
      </div>
    </>
  );
}
